// components/b2b/VendorConnections.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { DB } from '../../utils/db';
import { VENDOR_PRESETS } from '../../config/vendors';

export function VendorConnections({ user, onNavigate, onSyncComplete }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [connections, setConnections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState(null);
  
  useEffect(() => {
    loadConnections();
  }, []);
  
  const loadConnections = async () => {
    const conns = await DB.get('d2p_vendors') || [];
    setConnections(conns);
  };
  
  const handleSync = async (connId) => {
    const conn = connections.find(c => c.id === connId);
    if (!conn) return;
    
    toast.addToast('Starting sync...');
    
    // Simulate sync process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const success = Math.random() > 0.1;
    
    const updatedConns = connections.map(c => {
      if (c.id === connId) {
        if (success) {
          return {
            ...c,
            status: 'connected',
            lastSync: new Date().toISOString(),
            productCount: Math.floor(Math.random() * 40 + 10)
          };
        } else {
          return {
            ...c,
            status: 'error',
            error: 'Sync failed - Check credentials',
            lastSync: new Date().toISOString()
          };
        }
      }
      return c;
    });
    
    await DB.set('d2p_vendors', updatedConns);
    setConnections(updatedConns);
    
    if (success) {
      toast.addToast('Sync completed successfully');
      onSyncComplete && onSyncComplete();
    } else {
      toast.addToast('Sync failed', 'error');
    }
  };
  
  const handleDelete = async (connId) => {
    if (!confirm('Are you sure you want to remove this vendor connection?')) return;
    
    const filtered = connections.filter(c => c.id !== connId);
    await DB.set('d2p_vendors', filtered);
    setConnections(filtered);
    toast.addToast('Vendor connection removed');
  };
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 32,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              Vendor Connections
            </h1>
            <p style={{ color: '#6B7280' }}>
              Manage your GPO and distributor integrations
            </p>
          </div>
          <Button icon="plus" onClick={() => setShowModal(true)}>
            Connect Vendor
          </Button>
        </div>
        
        {/* Info Banner */}
        <div style={{
          background: '#EFF6FF',
          padding: 20,
          borderRadius: 12,
          marginBottom: 32,
          display: 'flex',
          gap: 16,
          alignItems: 'start'
        }}>
          <Icon name="alert" size={24} color="#3B82F6" />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#1E40AF' }}>
              How Vendor Connections Work
            </h3>
            <p style={{ fontSize: 14, color: '#3B82F6', lineHeight: 1.6, marginBottom: 12 }}>
              Connect your existing vendor accounts to automatically import pricing. We'll securely sync your 
              catalog data and show side-by-side comparisons with our direct pricing.
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: '#3B82F6', lineHeight: 1.8 }}>
              <li>Your credentials are encrypted and stored securely</li>
              <li>We sync pricing data only - no orders are placed automatically</li>
              <li>You choose when to buy direct vs through your vendor</li>
            </ul>
          </div>
        </div>
        
        {/* Connections List */}
        {connections.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="link" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                No Vendors Connected
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24, maxWidth: 500, margin: '0 auto 24px' }}>
                Connect your existing GPO and distributor accounts to import pricing and compare with direct costs
              </p>
              <Button icon="plus" onClick={() => setShowModal(true)}>
                Connect Your First Vendor
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gap: 20 }}>
            {connections.map(conn => (
              <Card key={conn.id}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'start' }}>
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: 12,
                    background: `${brand.c.accent}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    flexShrink: 0
                  }}>
                    🏥
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 8,
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: brand.c.navy,
                        margin: 0
                      }}>
                        {conn.name}
                      </h3>
                      <Badge status={conn.status} />
                      {conn.type && (
                        <span style={{
                          padding: '4px 8px',
                          background: '#F3F4F6',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#6B7280'
                        }}>
                          {conn.type}
                        </span>
                      )}
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 12,
                      fontSize: 14,
                      color: '#6B7280',
                      marginBottom: 12
                    }}>
                      <div>
                        <strong style={{ color: '#374151' }}>URL:</strong>{' '}
                        <a href={conn.url} target="_blank" rel="noopener noreferrer" style={{ color: brand.c.accent }}>
                          {conn.url}
                        </a>
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Username:</strong> {conn.username}
                      </div>
                      {conn.accountNumber && (
                        <div>
                          <strong style={{ color: '#374151' }}>Account:</strong> {conn.accountNumber}
                        </div>
                      )}
                      <div>
                        <strong style={{ color: '#374151' }}>Last Sync:</strong>{' '}
                        {conn.lastSync ? new Date(conn.lastSync).toLocaleString() : 'Never'}
                      </div>
                    </div>
                    
                    {conn.status === 'connected' && conn.productCount > 0 && (
                      <div style={{
                        padding: '8px 12px',
                        background: '#D1FAE5',
                        borderRadius: 6,
                        fontSize: 13,
                        color: '#065F46',
                        display: 'inline-block',
                        marginBottom: 12
                      }}>
                        ✓ {conn.productCount} products synced
                      </div>
                    )}
                    
                    {conn.status === 'error' && conn.error && (
                      <div style={{
                        padding: 12,
                        background: '#FEE2E2',
                        borderRadius: 8,
                        fontSize: 14,
                        color: '#991B1B',
                        marginBottom: 12,
                        display: 'flex',
                        gap: 8,
                        alignItems: 'start'
                      }}>
                        <Icon name="alert" size={18} color="#991B1B" />
                        <span>{conn.error}</span>
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button
                        size="sm"
                        icon="sync"
                        onClick={() => handleSync(conn.id)}
                      >
                        {conn.status === 'error' ? 'Retry Sync' : 'Re-sync'}
                      </Button>
                      
                      {conn.productCount > 0 && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon="grid"
                          onClick={() => setExpandedVendor(expandedVendor === conn.id ? null : conn.id)}
                        >
                          {expandedVendor === conn.id ? 'Hide' : 'View'} Products
                        </Button>
                      )}
                      
                      {conn.status === 'error' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon="msgCircle"
                          onClick={() => toast.addToast('Contact support for help with vendor sync issues')}
                        >
                          Get Help
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="danger"
                        icon="trash"
                        onClick={() => handleDelete(conn.id)}
                      >
                        Remove
                      </Button>
                    </div>
                    
                    {/* Expanded Products View */}
                    {expandedVendor === conn.id && conn.productCount > 0 && (
                      <div style={{
                        marginTop: 16,
                        padding: 16,
                        background: '#F9FAFB',
                        borderRadius: 8
                      }}>
                        <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: brand.c.navy }}>
                          Synced Products ({conn.productCount})
                        </h4>
                        <div style={{ fontSize: 13, color: '#6B7280' }}>
                          Product details available in catalog view with side-by-side pricing comparison
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        
        {/* Connect Vendor Modal */}
        {showModal && (
          <VendorModal
            brand={brand}
            onClose={() => setShowModal(false)}
            onConnect={async (vendorData) => {
              const newConn = {
                id: `conn_${Date.now()}`,
                ...vendorData,
                status: 'syncing',
                createdAt: new Date().toISOString(),
                lastSync: null,
                productCount: 0
              };
              
              const updatedConns = [...connections, newConn];
              await DB.set('d2p_vendors', updatedConns);
              setConnections(updatedConns);
              setShowModal(false);
              
              // Auto-sync after connection
              setTimeout(() => handleSync(newConn.id), 500);
            }}
          />
        )}
      </div>
    </div>
  );
}

// Vendor Connection Modal Component
function VendorModal({ brand, onClose, onConnect }) {
  const [step, setStep] = useState(1);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    username: '',
    password: '',
    accountNumber: '',
    notes: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  
  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      ...formData,
      name: vendor.name,
      url: vendor.url,
      type: vendor.type
    });
    setStep(2);
  };
  
  const handleSubmit = () => {
    if (!formData.name || !formData.username || !formData.password) {
      toast.addToast('Please fill all required fields', 'error');
      return;
    }
    
    onConnect(formData);
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 24,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.2s ease-out'
      }}>
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: brand.c.navy }}>
            {step === 1 ? 'Select Vendor' : `Connect to ${selectedVendor?.name}`}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4
            }}
          >
            <Icon name="x" size={24} color="#9CA3AF" />
          </button>
        </div>
        
        <div style={{ padding: 32 }}>
          {step === 1 ? (
            <>
              <p style={{ color: '#6B7280', marginBottom: 24 }}>
                Choose your vendor from the list below or add a custom connection
              </p>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 16
              }}>
                {VENDOR_PRESETS.map(vendor => (
                  <Card
                    key={vendor.id}
                    hover
                    onClick={() => handleVendorSelect(vendor)}
                    style={{ padding: 20 }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>
                        {vendor.logo}
                      </div>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>
                        {vendor.name}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: '#6B7280',
                        padding: '2px 8px',
                        background: '#F3F4F6',
                        borderRadius: 4,
                        display: 'inline-block'
                      }}>
                        {vendor.type}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={{
                background: '#EFF6FF',
                padding: 16,
                borderRadius: 8,
                marginBottom: 24,
                fontSize: 14,
                color: '#1E40AF',
                display: 'flex',
                gap: 12,
                alignItems: 'start'
              }}>
                <Icon name="lock" size={20} color="#3B82F6" />
                <div>
                  <strong>Security Note:</strong> Your credentials are encrypted and stored securely. 
                  We use them only to sync pricing data from your vendor portal.
                </div>
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600 }}>
                  Vendor Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600 }}>
                  Portal URL <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={e => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600 }}>
                  Username/Email <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600 }}>
                  Password <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      paddingRight: 48,
                      border: '2px solid #E5E7EB',
                      borderRadius: 8,
                      fontSize: 14,
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4
                    }}
                  >
                    <Icon name={showPassword ? 'eyeOff' : 'eye'} size={20} color="#9CA3AF" />
                  </button>
                </div>
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600 }}>
                  Account Number (Optional)
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={e => setFormData({ ...formData, accountNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <Button onClick={() => setStep(1)} variant="secondary">
                  Back
                </Button>
                <Button onClick={handleSubmit} icon="link" fullWidth>
                  Connect & Sync
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}